/**
 * useSmartScrollAndNewMessages — Vincent B596 (Eric 2026-07-28 SPEC STRICK)
 *
 * Hook factorisé partagé entre les composants de messagerie (AgoraPage,
 * ChannelsPanel). Gère :
 *   1. Scroll intelligent : on ne ramène le user en bas que s'il est déjà
 *      proche du bas (≤ threshold px) OU vient d'envoyer un message OU
 *      vient d'ouvrir la conversation/canal.
 *   2. Compteur de messages non-vus quand user lit l'historique.
 *   3. Micro-animation pulse (2 s) sur la transition 0 → N pour attirer
 *      l'œil sans être intrusif. Daltonien-safe.
 *   4. Reset auto quand user revient à ≤ threshold px du bas.
 *   5. Baseline correct : l'historique du 1er chargement n'est PAS compté
 *      comme « nouveaux ».
 *
 * Usage :
 *   const {
 *     messagesContainerRef, messagesEndRef, onMessagesScroll,
 *     newMsgCount, pulse, scrollToLatest, markJustSent, forceScrollOnNextRender,
 *   } = useSmartScrollAndNewMessages(messages, activeId);
 *
 *   // Dans le JSX :
 *   <div className="relative"><div ref={messagesContainerRef} onScroll={onMessagesScroll}>
 *     ... messages ...
 *     <div ref={messagesEndRef} />
 *   </div>
 *   {newMsgCount > 0 && (
 *     <button onClick={scrollToLatest} className={pulse ? "animate-bounce" : ""}>
 *       ↓ {newMsgCount} nouveau(x)
 *     </button>
 *   )}</div>
 *
 *   // Dans sendMessage() : markJustSent() avant setMessages(...)
 *   // Dans openConvo() : forceScrollOnNextRender()
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { isSoundEnabled, playDing } from "../lib/messagingSound";

const BOTTOM_THRESHOLD_PX = 120;
const PULSE_DURATION_MS = 2000;

export function useSmartScrollAndNewMessages(messages, activeId) {
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const justSentRef = useRef(false);
  const forceBottomOnOpenRef = useRef(false);
  const prevMessagesLenRef = useRef(0);
  const [newMsgCount, setNewMsgCount] = useState(0);
  const [pulse, setPulse] = useState(false);

  // Reset baseline quand le contexte change (nouveau canal / nouvelle conversation)
  useEffect(() => {
    setNewMsgCount(0);
    setPulse(false);
    prevMessagesLenRef.current = 0;
  }, [activeId]);

  // Stop pulse après 2 s
  useEffect(() => {
    if (!pulse) return;
    const t = setTimeout(() => setPulse(false), PULSE_DURATION_MS);
    return () => clearTimeout(t);
  }, [pulse]);

  // Effet principal : décide si on scroll et/ou incrémente le compteur
  useEffect(() => {
    const prevLen = prevMessagesLenRef.current;
    const currLen = messages?.length || 0;
    prevMessagesLenRef.current = currLen;

    if (forceBottomOnOpenRef.current) {
      forceBottomOnOpenRef.current = false;
      setNewMsgCount(0);
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      return;
    }
    if (justSentRef.current) {
      justSentRef.current = false;
      setNewMsgCount(0);
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    const container = messagesContainerRef.current;
    if (!container) return;

    // Baseline : la 1re fois qu'on reçoit des messages (prevLen=0), on scroll
    // en bas sans incrémenter le compteur (sinon l'historique complet serait
    // compté comme « nouveaux »).
    if (prevLen === 0 && currLen > 0) {
      setNewMsgCount(0);
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      return;
    }

    // Vincent B596 récurrence (Eric 2026-07-30 SPEC STRICK) — Ne scroller
    // que quand il y a de VRAIS nouveaux messages (`currLen > prevLen`).
    // Auparavant on scrollait à chaque re-render (le polling toutes les
    // 4 s reconstruit `messages` avec une nouvelle référence même si le
    // contenu est identique → useEffect fire → distanceFromBottom ≤ 120px
    // → scrollIntoView).  Résultat : l'utilisateur qui a scrollé de 80 px
    // vers le haut était ramené en bas au premier poll suivant.
    // Correctif : le simple fait d'être « proche du bas » ne suffit plus à
    // déclencher un scroll — il faut un nouveau message.
    if (currLen === prevLen) return;

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (currLen > prevLen) {
      if (distanceFromBottom <= BOTTOM_THRESHOLD_PX) {
        // User déjà en bas — on suit naturellement le flux
        setNewMsgCount(0);
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      } else {
        // User lit l'historique — on incrémente le compteur, PAS de scroll
        setNewMsgCount((n) => {
          if (n === 0) {
            setPulse(true);
            // Eric 2026-07-30 (B596 enhancement) — Ding audio discret UNIQUEMENT
            // sur la transition 0 → N (nouveau message reçu pendant lecture
            // d'historique). Silencieux si l'utilisateur n'a pas activé la
            // préférence (`knoria_msg_sound_enabled`). Muet par défaut.
            if (isSoundEnabled()) playDing();
          }
          return n + (currLen - prevLen);
        });
      }
    }
    // Sinon (currLen < prevLen : suppression de message) : on ne touche pas
    // à la position de scroll — laissez l'utilisateur là où il est.
  }, [messages]);

  const onMessagesScroll = useCallback(() => {
    const c = messagesContainerRef.current;
    if (!c) return;
    const distanceFromBottom = c.scrollHeight - c.scrollTop - c.clientHeight;
    if (distanceFromBottom <= BOTTOM_THRESHOLD_PX) {
      setNewMsgCount((n) => (n === 0 ? n : 0));
    }
  }, []);

  const scrollToLatest = useCallback(() => {
    setNewMsgCount(0);
    setPulse(false);
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const markJustSent = useCallback(() => { justSentRef.current = true; }, []);
  const forceScrollOnNextRender = useCallback(() => { forceBottomOnOpenRef.current = true; }, []);

  return {
    messagesContainerRef,
    messagesEndRef,
    onMessagesScroll,
    newMsgCount,
    pulse,
    scrollToLatest,
    markJustSent,
    forceScrollOnNextRender,
  };
}
